import { Button } from "../../../components";
import React, { useCallback, useEffect, useState } from "react";
import { GAEventTrack } from "../../../utils/hooks/GA_events";
import { auth, authMobile, db, dbMobile } from "../../../config/firebaseConfig";
import envConfig from "../../../config/envConfig";
import axios from "axios";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDocs,
  query,
  where,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import AppStoreButtons from "../../../components/AppStoreButtons";
import { FaCircleCheck } from "react-icons/fa6";

const PractidoAppPaymentModule = ({ schoolDetails, parentsDetails }) => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState("idle"); // idle | creating_order | opening_checkout | verifying | saving | success | failed
  const [isOfferValid, setIsOfferValid] = useState(false);
  const [isOfferAvailed, setIsOfferAvailed] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [phone, setPhone] = useState(parentsDetails?.phone || "");
  const [fee, setFee] = useState(99);
  useEffect(() => {
    if (schoolDetails) {
      setIsOfferValid(true);
      setFee(schoolDetails?.fee);
    }
  }, [schoolDetails]);

  useEffect(() => {
    setPhone(parentsDetails?.phone || "");
  }, [parentsDetails]);

  // Check if the special app offer has already been availed (web Firestore)
  useEffect(() => {
    const checkOfferAvailed = async () => {
      try {
        const userId = parentsDetails?.id;
        if (!userId) return;

        const paymentsRef = collection(db, "payments");
        const filters = [
          where("userId", "==", userId),
          where("appOfferAvailed", "==", true),
        ];

        const q = query(paymentsRef, ...filters);
        const snap = await getDocs(q);
        const availed = !snap.empty;
        setIsOfferAvailed(availed);
      } catch (err) {
        setIsOfferAvailed(false);
      }
    };

    checkOfferAvailed();
  }, [schoolDetails, parentsDetails]);

  const savePaymentDetails = useCallback(
    async (response, order) => {
      // payment details for payment collection
      const paymentDocId = response.razorpay_payment_id;
      const paymentDetails = {
        orderId: response.razorpay_order_id,
        amount: order.amount,
        currency: order.currency,
        paymentId: response.razorpay_payment_id,
        paymentSignature: response.razorpay_signature,
        uid: auth.currentUser.uid || "",
        userId: parentsDetails?.id,
        appOfferAvailed: true,
        schoolId: schoolDetails?.schoolId || null,
        status: "verified",
        paymentAt: Timestamp.fromDate(new Date()),
      };

      // Add payment details to Firebase Firestore
      try {
        const batch = writeBatch(db);
        const paymentRef = doc(db, "payments", paymentDocId);
        batch.set(paymentRef, paymentDetails, { merge: true });

        // payment details for parent collection
        const paymentDetailsParentDoc = {
          paymentId: response.razorpay_payment_id,
          amount: order.amount,
          currency: order.currency,
          paymentAt: Timestamp.fromDate(new Date()),
          paymentRef: paymentRef.id,
        };

        const docId = parentsDetails?.id;
        // update data to parent db
        const parentDocRef = doc(db, "parents", docId);
        const newPaymentArray = [
          ...(parentsDetails?.paymentDetails || []).filter(
            (d) => d?.paymentId !== paymentDetailsParentDoc.paymentId
          ),
          paymentDetailsParentDoc,
        ];
        batch.update(parentDocRef, { paymentDetails: newPaymentArray });

        await batch.commit();

        const mobileUid = authMobile?.currentUser?.uid;
        if (mobileUid) {
          const nowMs = Date.now();
          const startTimeMillis = String(nowMs);
          // const expiryDate = new Date(nowMs);

          const expiryDate = new Date(nowMs);
          expiryDate.setMonth(expiryDate.getMonth() + 1);
          const maxExpiry = new Date("2026-03-31T23:59:59Z").getTime();
          const expiryTimeMillis = String(maxExpiry);

          const data = {
            startTimeMillis: startTimeMillis,
            expiryTimeMillis: expiryTimeMillis,
            autoRenewing: true,
            priceCurrencyCode: "INR",
            priceAmountMicros: ((fee || 99) * 1000000).toString(),
            countryCode: "IN",
            developerPayload: "",
            paymentState: 1,
            paymentRef: paymentRef.id,
            purchaseType: 0,
            acknowledgementState: 1,
            kind: "androidpublisher#subscriptionPurchase",
            platform: "android",
            is_practido_offer: true,
          };

          // Save under collection `subscription` with document id = uid
          try {
            await setDoc(doc(dbMobile, "subscription", mobileUid), data);
          } catch (e) {
            // flag pending mobile sync
            await updateDoc(doc(db, "payments", paymentDocId), {
              subscriptionPending: true,
            });
          }
        }

        //GA event tracking
        GAEventTrack({
          event: "payment_data_saved",
          data: {
            uid: auth?.currentUser?.uid,
            email: auth?.currentUser?.email,
          },
        });
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
    [
      parentsDetails?.id,
      parentsDetails?.paymentDetails,
      schoolDetails?.schoolId,
      fee,
    ]
  );

  // Retry pending save from a prior successful payment
  useEffect(() => {
    const retryDataRaw = sessionStorage.getItem("practido-payment-retryData");
    if (!retryDataRaw) return;
    try {
      const retryData = JSON.parse(retryDataRaw);
      if (retryData?.response && retryData?.order) {
        (async () => {
          try {
            setStage("saving");
            setLoading(true);
            await savePaymentDetails(retryData.response, retryData.order);
            sessionStorage.removeItem("practido-payment-retryData");
            setPaymentSuccess(true);
            setError("");
          } catch (e) {
            // keep retry data
          } finally {
            setLoading(false);
            setStage("idle");
          }
        })();
      }
    } catch {}
  }, [savePaymentDetails]);

  const loadRazorpayScript = async () => {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (e, amount = 99) => {
    e.preventDefault();
    setError("");

    if (!phone || String(phone).trim().length < 10) {
      setError("Please enter a valid phone number");
      return;
    }
    if (!isOfferValid) {
      setError("Offer is not valid for this account");
      return;
    }
    if (isOfferAvailed) {
      setError("Offer has already been availed");
      return;
    }

    const backendUrl = `${envConfig.backendUrl}`;
    const currency = "INR";

    try {
      setLoading(true);
      setStage("creating_order");

      if (!window.Razorpay) {
        await loadRazorpayScript();
      }

      const {
        data: { key },
      } = await axios.get(`${backendUrl}?action=getkey`, { timeout: 10000 });

      const {
        data: { order },
      } = await axios.post(
        `${backendUrl}?action=checkout`,
        {
          amount,
          currency,
        },
        { timeout: 12000 }
      );

      console.log(order);

      const options = {
        key,
        order_id: order.id,
        amount: order.amount,
        currency: order.currency,
        name: envConfig.appFullName,
        description: envConfig.appFullName,
        image: "/img/appLogo.png",
        prefill: {
          name: auth?.currentUser?.displayName,
          email: auth?.currentUser?.email,
          contact: phone,
        },
        notes: {
          uid: auth?.currentUser?.uid,
        },
        theme: {
          color: "#E74C3C",
        },
        handler: async (response) => {
          try {
            setStage("verifying");
            await paymentSuccessHandler(response, backendUrl, order);
          } catch {
            setError(error?.message);
            console.log("failed");
          }
        },
        modal: {
          ondismiss: async () => await onPaymentModalDismiss(backendUrl, order),
        },
      };

      console.log(options);

      const razor = new window.Razorpay(options);
      // Handle payment failure
      razor.on("payment.failed", function (response) {
        setStage("failed");
        setLoading(false);
        setError("Payment failed. Please try again.");
        GAEventTrack({
          event: "step1_payment_failed",
          data: {
            uid: auth?.currentUser?.uid,
            email: auth?.currentUser?.email,
            reason:
              response?.error?.description ||
              response?.error?.code ||
              "unknown",
            orderId: order?.id,
          },
        });
      });
      setStage("opening_checkout");
      razor.open();
      //   setLoadingModule(true);
    } catch (error) {
      // GA event tracking
      //   GAEventTrack({
      //     event: "step1_payment_init_failed",
      //     data: {
      //       uid: auth?.currentUser?.uid,
      //       email: auth?.currentUser?.email,
      //       error: error.code,
      //     },
      //   });
      console.log(error);
      setError(
        "Something went wrong while initiating your payment. Please try again."
      );
    } finally {
      if (stage !== "verifying" && stage !== "saving") {
        setLoading(false);
      }
    }
  };

  const onPaymentModalDismiss = async (backendUrl, order) => {
    await axios.post(`${backendUrl}?action=orderDismissed`, {
      orderId: order.id,
    });
    // GA event tracking
    GAEventTrack({
      event: "step1_payment_dismissed",
      data: {
        uid: auth?.currentUser?.uid,
        email: auth?.currentUser?.email,
      },
    });
  };
  const paymentSuccessHandler = async (response, backendUrl, order) => {
    console.log("success handler ", response);

    // Payment verification
    try {
      const verificationResponse = await axios.post(
        `${backendUrl}?action=paymentVerification`,
        {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
          // userId: ,
          // userEmail: auth?.currentUser?.email,
          receipt: order?.receipt,
          amount: order?.amount,
          currency: order?.currency,
        },
        { timeout: 12000 }
      );

      if (verificationResponse?.data?.status === "success") {
        console.log("Payment verification successful");
        try {
          setStage("saving");
          await savePaymentDetails(response, order);
          setPaymentSuccess(true);
          // show payment success module
        } catch (error) {
          console.log("Error saving payment details:", error);
          sessionStorage.setItem(
            "practido-payment-retryData",
            JSON.stringify({ response, order })
          );
          setError("We received your payment, but saving failed. Retry below.");
          //GA event tracking
          GAEventTrack({
            event: "step1_payment_data__saving_failed",
            data: {
              uid: auth?.currentUser?.uid,
              email: auth?.currentUser?.email,
            },
          });
        }
      } else {
        console.log("Payment verification failed");
        //GA event tracking
        GAEventTrack({
          event: "step1_payment_verification_failed",
          data: {
            uid: auth?.currentUser?.uid,
            email: auth?.currentUser?.email,
          },
        });
        throw new Error("Payment verification failed");
      }
    } catch (error) {
      console.log(error);
      GAEventTrack({
        event: "step1_paymentVerification_serverError",
        data: {
          uid: auth.currentUser?.uid,
          email: auth?.currentUser?.email,
        },
      });
      throw new Error(
        ("server error - Somethig went wrong while verifiying your payment.",
        error)
      );
    }
  };

  //   const retryUpdatingPaymentData = async () => {
  //     const data = JSON.parse(localStorage.getItem("practido-step1-retryData"));
  //     if (data) {
  //       const response = data.response;
  //       const order = data.order;
  //       const paymentFor = data.paymentFor;
  //       try {
  //         await savePaymentDetails(response, order, paymentFor);
  //         setRetryModule(false);
  //         window.location.href = `/onboarding/step2?ref=${response.razorpay_payment_id}`;
  //       } catch (error) {
  //         console.log("Error saving payment details:", error);
  //         localStorage.setItem(
  //           "practido-step1-retryData",
  //           JSON.stringify({
  //             response,
  //             order,
  //             paymentFor,
  //           })
  //         );
  //         setRetryModule(true);
  //       }
  //     }
  //   };

  return (
    <>
      {error ? (
        <div className="w-full mx-auto max-w-3xl rounded-md border-2 border-red-100 bg-red-50 p-3 mb-3 text-sm text-red-800">
          {error}
          {stage === "saving" && (
            <div className="mt-2">
              <button
                className="underline text-red-700"
                onClick={async () => {
                  try {
                    setLoading(true);
                    const retryRaw = sessionStorage.getItem(
                      "practido-payment-retryData"
                    );
                    if (!retryRaw) return;
                    const retryData = JSON.parse(retryRaw);
                    await savePaymentDetails(
                      retryData.response,
                      retryData.order
                    );
                    sessionStorage.removeItem("practido-payment-retryData");
                    setPaymentSuccess(true);
                    setError("");
                  } catch (e) {
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Retry saving
              </button>
            </div>
          )}
        </div>
      ) : null}
      {paymentSuccess ? (
        <div className="w-full mx-auto max-w-3xl rounded-xl border-2 border-green-100 bg-green-50 p-4 sm:p-6">
          <div className="flex flex-col items-start gap-3 md:flex-row md:items-center md:gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-4">
                <FaCircleCheck className="text-green-400 w-20 h-20" />
                <h3 className="text-base font-semibold text-green-700 sm:text-lg">
                  <span>
                    Payment Successful! <br />
                    Welcome to Practido - Olympiad Practice!
                  </span>
                </h3>
              </div>

              <p className="mt-2 text-sm sm:text-base">
                Your child now has unlimited practice in Math, Science,
                Computers & English.
              </p>
              <p className="mt-1 text-sm sm:text-base bg-blue-50 p-2 px-4 rounded-lg border-l-4 border-blue-400">
                <strong className="text-blue-800">Note :</strong>
                <br />
                <span className="text-sm">
                  Please use your registered email ID and the password you
                  created during registration to log in to the Practido -
                  Olympiad Practice App.
                </span>
              </p>
              <p className="mt-1 text-sm sm:text-base">
                Log in anytime and start preparing for Olympiads & exams.
              </p>

              <div className="mt-4 rounded-md p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-800 sm:text-base">
                  Download Practido Online Olympiad
                </h4>
                <p className="mt-1 text-sm text-gray-600">
                  10,000+ installs, Teacher Approved by Google, and a 4.5★ Play
                  Store and App Store rating
                </p>
                <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-700 sm:text-base">
                  <li>
                    Unlimited practice in Math, Science, Computers & English
                    (Class 1–8)
                  </li>
                  <li>
                    Gamified challenges – trophies, leaderboard, points &
                    rewards
                  </li>
                  <li>
                    Challenge your friends – make Olympiad practice exciting and
                    fun
                  </li>
                  <li>Instant results & progress tracking</li>
                  <li>
                    Check solutions for every question – learn from mistakes
                    instantly
                  </li>
                  <li>Anytime, anywhere learning without extra workload</li>
                  <li>
                    No waiting for the next book, no running out of material –
                    just practice & improvement
                  </li>
                </ul>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <AppStoreButtons variant="black" />
                </div>
              </div>

              <div className="mt-4 grid gap-2 text-sm text-gray-700 sm:text-base">
                <div>Access activated instantly</div>
                <div>You paid only ₹{fee || 99} through your school offer</div>
                <div>Valid till this Academic Year (2024-2025)</div>
                <div>
                  Need help? Contact us at{" "}
                  <a
                    className="text-green-700 underline"
                    href={`mailto:${envConfig.appEmail}`}
                  >
                    {envConfig.appEmail || "hello@practido.com"}
                  </a>{" "}
                  |
                  <a
                    className="ml-1 text-green-700 underline"
                    href="tel:9560734660"
                  >
                    9560734660
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : isOfferValid && !isOfferAvailed ? (
        <div className="w-full mx-auto max-w-4xl rounded-xl border-2 border-gray-100 bg-white p-4 sm:p-6">
          <h3 className="text-base font-semibold text-gray-900 sm:text-lg md:text-xl">
            Secure Your {envConfig.appFullName} – Special School Discount!
          </h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:gap-6">
            <div className="rounded-xl border-2 border-gray-300 bg-gray-50 p-4">
              <div className="text-sm font-medium text-gray-700">
                Special Academic Year Offer
              </div>
              <div className="mt-1 text-2xl font-bold text-green-800">
                ₹{fee || 99}
              </div>
              <div className="text-xs text-gray-600">
                One-time fee per student (valid for this academic year)
              </div>
              <div className="mt-2 inline-flex items-center rounded-md bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                Offer applied automatically
              </div>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-700 sm:text-base">
                <li>
                  Unlimited practice in Math, Science, Computers & English
                  (Classes 1–8)
                </li>
                <li>
                  Gamified challenges – trophies, leaderboard, points & rewards
                </li>
                <li>
                  Challenge your friends – make Olympiad practice exciting and
                  fun
                </li>
                <li>Instant results & progress tracking</li>
                <li>
                  Check solutions for every question – learn from mistakes
                  instantly
                </li>
                <li>Anytime, anywhere learning without extra workload</li>
                <li>
                  No waiting for the next book, no running out of material –
                  just practice & improvement
                </li>
              </ul>
            </div>
            <form
              onSubmit={(e) => handlePayment(e, fee)}
              className="rounded-xl border-2 border-gray-300 p-4"
            >
              <div className="text-sm font-medium text-gray-900">Checkout</div>
              <div className="mt-3 grid gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600">
                    Name
                  </label>
                  <input
                    type="text"
                    value={auth?.currentUser?.displayName || ""}
                    readOnly
                    className="mt-1 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-700 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">
                    Email
                  </label>
                  <input
                    type="email"
                    value={auth?.currentUser?.email || ""}
                    readOnly
                    className="mt-1 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-700 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none"
                    placeholder="Enter phone number"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">
                    Amount
                  </label>
                  <input
                    type="text"
                    value={`₹${fee || 99}`}
                    readOnly
                    className="mt-1 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-700 focus:outline-none"
                  />
                </div>
                <div className="pt-1">
                  <div className="w-full sm:w-auto">
                    <Button
                      text={loading ? "Processing..." : `Pay ₹${fee || 99}`}
                      type="submit"
                      version="secondary"
                      loading={loading}
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : !isOfferValid ? (
        <div className="w-full mx-auto max-w-3xl rounded-xl border-2 border-yellow-100 bg-yellow-50 p-4 sm:p-6">
          <div className="flex flex-col items-start gap-3">
            <h3 className="text-base font-semibold text-yellow-800 sm:text-lg">
              Offer not valid
            </h3>
            <p className="text-sm text-yellow-900">
              This special school offer is not available for your account.
            </p>
          </div>
        </div>
      ) : isOfferAvailed ? (
        <div className="w-full mx-auto max-w-3xl rounded-xl border-2 border-blue-100 bg-blue-50 p-4 sm:p-6">
          <div className="flex flex-col items-start gap-3">
            <h3 className="text-base font-semibold text-blue-800 sm:text-lg">
              Offer already availed
            </h3>
            <p className="text-sm text-blue-900">
              You have already used this school discount for your account.
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default PractidoAppPaymentModule;
